import { useEffect, useMemo, useState } from 'react';

/**
 * Smart Template Engine — Preview System shared TIMELINE ENGINE (Phase 2A).
 *
 * Pure, reusable "which item is showing now" math. The wall player does NOT write
 * its current item to RTDB, so the live preview (3A) computes it the same way the
 * device does: from a start instant + per-item durations. Reused by 2A (local
 * clock), 4 (simulator clock / schedule scrub), and 3A (RTDB start_timestamp +
 * serverTimeOffset). Read-only — zero sync contact.
 */

/** Locate the item + in-item offset for a given elapsed time (ms). */
export function locateItem(durationsMs, elapsedMs, loop = true) {
  const total = (durationsMs || []).reduce((a, b) => a + (b > 0 ? b : 0), 0);
  if (!durationsMs?.length || total <= 0) return { index: 0, offsetMs: 0, elapsedMs: 0, totalMs: 0 };
  let e = Math.max(0, elapsedMs);
  e = loop ? e % total : Math.min(e, total - 1);
  let acc = 0;
  for (let i = 0; i < durationsMs.length; i += 1) {
    const d = durationsMs[i] > 0 ? durationsMs[i] : 0;
    if (e < acc + d) return { index: i, offsetMs: e - acc, elapsedMs: e, totalMs: total };
    acc += d;
  }
  const last = durationsMs.length - 1;
  return { index: last, offsetMs: durationsMs[last] || 0, elapsedMs: e, totalMs: total };
}

/** Re-render on an interval while `playing` (drives clock-based components). */
export function useTick(playing, tickMs = 250) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => force((x) => (x + 1) % 1e9), tickMs);
    return () => clearInterval(t);
  }, [playing, tickMs]);
}

/**
 * Ticking timeline. `getElapsed()` returns the current elapsed ms (the caller owns
 * the clock — local for 2A/4, serverNow()-startTimestamp for 3A).
 * @returns {{index, offsetMs, elapsedMs, totalMs}}
 */
export function useWallTimeline({ durationsMs, getElapsed, playing = true, loop = true, tickMs = 250 }) {
  useTick(playing, tickMs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => locateItem(durationsMs, getElapsed(), loop), [durationsMs, loop, playing, getElapsed, Math.floor(getElapsed() / tickMs)]);
}
